/**
 * TypeScript interfaces for Bridge Session flow
 * 
 * Aligned with backend BRIDGE_SESSION_FLOW.md architecture
 */

export type BridgeSessionStep =
  | 'questions'
  | 'domain_builder'
  | 'entropy_heat_map'
  | 'friction_priorities'
  | 'define_conditions'
  | 'bridge_card'
  | 'control_bridge';

/** Product Domain Bridge 11-step flow */
export type ProductDomainStep =
  | 'product_context'
  | 'product_mechanics'
  | 'current_performance'
  | 'baseline_trajectory'
  | 'future_target'
  | 'product_entropy'
  | 'product_heat_map'
  | 'bridge_focus'
  | 'friction_priorities'
  | 'define_transition'
  | 'bridge_card'
  | 'control_activation';

export const PRODUCT_DOMAIN_STEPS: ProductDomainStep[] = [
  'product_context',
  'product_mechanics',
  'current_performance',
  'baseline_trajectory',
  'future_target',
  'product_entropy',
  'product_heat_map',
  'bridge_focus',
  'friction_priorities',
  'define_transition',
  'bridge_card',
  'control_activation',
];

/** Product Domain UI state */
export type ProductDomainState =
  | 'INITIALIZED'
  | 'DRAFTED'
  | 'READY_FOR_ENTROPY'
  | 'DIAGNOSED'
  | 'ACTIVE';

export type DomainState = 'sufficient' | 'in_progress' | 'locked';
export type EntropyHeatMapState = 'available' | 'locked';
export type BridgeCardState = 'incomplete' | 'complete' | 'active' | 'expired' | 'reviewed';
export type ControlBridgeState = 'available' | 'locked';

export interface ReportingPeriodBlock {
  artifact_item_id: string;
  start_date: string;
  end_date: string;
}

export interface ReportingPeriod {
  start_date: string;
  end_date: string;
  blocks: ReportingPeriodBlock[];
}

export interface SubdomainStatus {
  artifact_item_id: string;
  artifact_id: string;
  name: string;
  state: DomainState;
  correction_factor?: number;
  entropy_score?: number;
  reporting_period?: ReportingPeriod;
  improvement_artifact_item_id?: string;
  improvement_workflow_state?: 'IDENTIFIED' | 'DEFINED' | 'DESIGNED' | 'IMPLEMENTED' | 'VALIDATED';
  completedAt?: string;
}

export interface DomainStatus {
  artifact_id: string;
  name: string;
  state: DomainState;
  subdomains: SubdomainStatus[];
  correction_factor?: number;
  entropy_score?: number;
  completedAt?: string;
}

// Scope method types (defined before use in BridgeSessionState)
export type ScopeMethod = 'depth_limit' | 'descendant_list' | 'hybrid';
export type ScopeType = 'domain' | 'subdomain' | 'artifact_type' | 'date_range' | 'hierarchical_depth' | 'composite';

// Date range for scope
export interface ScopeDateRange {
  start_date: string;
  end_date: string;
  include_all_periods?: boolean;
}

// Scope definition (stored in improvement artifact_item)
export interface BridgeSessionScope {
  top_level_artifact_id: string;
  scope_method: ScopeMethod;
  depth_limit?: number;
  descendant_artifact_ids?: string[];
  date_range?: ScopeDateRange;
  artifact_type_filters?: string[];
  excluded_artifact_ids?: string[];
  include_transactions?: boolean;
  transaction_artifact_type_ids?: string[];
}

// Scope validation response
export interface ScopeValidation {
  valid: boolean;
  included_artifacts: Array<{
    artifact_id: string;
    artifact_name: string;
    depth: number;
    included_reason: string;
  }>;
  excluded_artifacts: Array<{
    artifact_id: string;
    artifact_name: string;
    excluded_reason: string;
  }>;
  included_artifact_count: number;
  included_subdomain_count: number;
  excluded_artifact_count: number;
}

// Scope configuration for creating Bridge Session
export interface ScopeConfiguration {
  scope_type: ScopeType;
  domain_ids?: string[];
  subdomain_ids?: string[];
  artifact_type_ids?: string[];
  date_range?: ScopeDateRange;
  max_depth?: number;
  include_children?: boolean;
  include_transactions?: boolean;
  // For composite scope
  composite?: {
    domain_ids?: string[];
    date_range?: ScopeDateRange;
    artifact_types?: string[];
    max_depth?: number;
  };
}

export interface BridgeSessionState {
  platform_initialized: boolean;
  control_bridge_id?: string;
  scope?: BridgeSessionScope; // Scope definition
  scope_validation?: ScopeValidation; // Scope validation results
  domains: {
    [domainId: string]: DomainStatus;
  };
  entropyHeatMap: {
    state: EntropyHeatMapState;
    scope_artifact_ids?: string[]; // Artifact IDs in scope
  };
  bridgeCard: {
    state: BridgeCardState;
    objective_artifact_item_id?: string;
    target_subdomain_artifact_item_id?: string;
  };
  controlBridge: {
    state: ControlBridgeState;
  };
}

export interface EntropyScore {
  artifact_item_id: string;
  artifact_id: string;
  artifact_name: string;
  domain_name: string;
  subdomain_name?: string;
  entropy_score: number; // 1-10
  correction_factor?: number;
  state: string; // ACTIVE, DRAFTED, CREATED, etc.
  ideal_value?: number;
  actual_value?: number;
  calculated_at: string;
}

export interface EntropyScoresResponse {
  scores: EntropyScore[];
  calculatedAt: string;
}

/** Metrics from control-bridge for Actual vs Ideal charts. */
export interface EntropyMapMetrics {
  current_revenue: number;
  current_expense: number;
  ideal_revenue: number;
  ideal_expense: number;
  recoverable_revenue: number;
  recoverable_expense: number;
  rem_current?: number;
  eem_current?: number;
  future_revenue?: number;
  future_expense?: number;
}

export interface EntropyMapResponse {
  scores: EntropyScore[];
  /** Optional overall Business entropy (for top-level summary). */
  business_entropy_score?: number;
  /** Optional metrics for Actual vs Ideal charts (when control-bridge/calculate available). */
  metrics?: EntropyMapMetrics;
  /** Product Domain: subdomain with highest entropy (for MarketFit branching CTA). */
  highest_entropy_subdomain?: string | null;
  /** Product Domain: gated bridge focus (MarketFit first when E_marketfit >= 6.5 and revenue low; Dev first when E_dev >= 7). */
  bridge_focus?: string | null;
  /** Product Domain: recommend full MarketFit guided session when E_marketfit >= 6.5 OR MF5 >= 8 OR MF1 >= 8. */
  marketfit_deep_dive_recommended?: boolean;
  hierarchy: {
    domain_id: string;
    domain_name: string;
    /** Rollup score for collapsed header color (e.g. max of factor scores in this group). */
    rollup_entropy_score?: number;
    subdomains: Array<{
      subdomain_id: string;
      subdomain_name: string;
      artifact_item_id: string;
      entropy_score: number;
      correction_factor?: number;
    }>;
  }[];
  calculated_at: string;
}

export interface CreateObjectiveRequest {
  target_subdomain_artifact_item_id: string;
  target_correction_factor: number;
  improvement_objective: string;
  // Scope is already defined in Bridge Session, but validate selection is in scope
  validate_scope?: boolean; // Optional flag to validate subdomain is in scope
}

export interface CreateObjectiveResponse {
  objective_artifact_item_id: string;
  objective_artifact_id: string;
  improvement_artifact_item_ids: string[];
  bridgeCardData: any; // BridgeCardObjective type
}

export interface PlatformInitStatus {
  initialized: boolean;
  control_bridge_id?: string;
  missing_components?: string[];
  validation_errors?: string[];
}

export interface DomainHierarchyResponse {
  control_bridge_id: string;
  domains: DomainStatus[];
}
